import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { sanitizeUsername, isValidUsername, getUsernameError } from "@/lib/usernameValidation";
import { PageMeta } from "@/components/PageMeta";

const f = "'IBM Plex Mono', monospace";
const HOVER_BLUE = "#0057FF";

const getPasswordStrength = (password: string) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  return score;
};

const strengthLabel = ["", "FRACA", "RAZOÁVEL", "BOA", "FORTE"];
const strengthColor = ["var(--lp-border2)", "#E55", "#E9A", "#8B5", "var(--lp-ink)"];

export default function CriarContaPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [googleHover, setGoogleHover] = useState(false);
  const [appleHover, setAppleHover] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Debounced username availability check
  useEffect(() => {
    if (!username || !isValidUsername(username)) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    const timeout = setTimeout(async () => {
      const { data } = await supabase.rpc("check_username_available", { p_username: username });
      setUsernameAvailable(!!data);
      setCheckingUsername(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [username]);

  const handleUsernameChange = useCallback((val: string) => {
    setUsername(sanitizeUsername(val).slice(0, 20));
  }, []);

  const strength = getPasswordStrength(password);
  const isFormValid =
    email.trim() &&
    password.length >= 8 &&
    confirmPassword.length > 0 &&
    acceptedTerms &&
    isValidUsername(username) &&
    usernameAvailable === true;

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError("as senhas não conferem.");
      return;
    }
    if (password.length < 8) {
      setError("a senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `https://${username}.criaaa.com/admin`,
      },
    });
    if (err) {
      setError("algo deu errado. tenta de novo?");
      setLoading(false);
    } else {
      if (data?.user) {
        try {
          await supabase.functions.invoke("send-confirmation-email", {
            body: { email: email.trim() },
          });
        } catch (emailError) {
          console.error("Failed to send confirmation email:", emailError);
        }
      }
      setEmailSent(true);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: "https://criaaa.com/auth/callback",
      },
    });
    if (error) console.error(error);
  };

  const handleAppleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        redirectTo: "https://criaaa.com/auth/callback",
      },
    });
    if (error) console.error(error);
  };


  const inputStyle: React.CSSProperties = {
    fontFamily: f,
    fontSize: "var(--type-body)",
    width: "100%",
    background: "transparent",
    border: "0.5px solid var(--lp-border2)",
    borderRadius: 0,
    padding: "0.85rem 1.25rem",
    color: "var(--lp-ink)",
    outline: "none",
    boxSizing: "border-box" as const,
  };

  const checks = [
    { ok: password.length >= 8, label: "mínimo 8 caracteres" },
    { ok: /[A-Z]/.test(password), label: "uma letra maiúscula" },
    { ok: /[0-9]/.test(password), label: "um número" },
    { ok: /[^A-Za-z0-9]/.test(password), label: "um caractere especial" },
  ];

  return (
    <div style={{ background: "var(--lp-bg)", minHeight: "100vh", fontFamily: f, color: "var(--lp-ink)" }}>
      <PageMeta title="Criar Conta — CRIAAA" description="Crie sua conta CRIAAA, escolha seu endereço e publique seu portfólio sem código." />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          minHeight: "100vh",
        }}
        className="criar-conta-grid"
      >
        {/* Left column — dark context */}
        <div
          className="criar-conta-left"
          style={{
            background: "var(--lp-ink)",
            color: "var(--lp-bg)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "3rem 4rem 4rem",
          }}
        >
          <div>
            <a
              href="https://criaaa.com"
              style={{
                fontFamily: f,
                fontSize: "1rem",
                fontWeight: 400,
                letterSpacing: "0.22em",
                textTransform: "uppercase" as const,
                textDecoration: "none",
                color: "var(--lp-bg)",
                display: "block",
                marginBottom: "4rem",
              }}
            >
              CRIAAA
            </a>
            <p style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", fontWeight: 300, lineHeight: 1.2, margin: 0 }}>
              Você já cria.
              <br />
              Agora apareça.
            </p>
          </div>
          <div style={{ marginTop: "4rem" }}>
            {["Portfólio com o seu domínio", "Simples de montar, bonito de ver", "Feito no Brasil, para quem cria"].map(
              (text) => (
                <p
                  key={text}
                  style={{
                    fontSize: "var(--type-body)",
                    color: "rgba(247,246,242,0.6)",
                    lineHeight: 2,
                    margin: 0,
                  }}
                >
                  → {text}
                </p>
              ),
            )}
          </div>
        </div>

        {/* Right column — form */}
        <div
          style={{
            background: "var(--lp-bg)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "4rem",
          }}
        >
          <div style={{ maxWidth: 480, width: "100%" }}>
            {emailSent ? (
              <div>
                <h1 style={{ fontSize: "var(--type-h2)", fontWeight: 300, marginBottom: "1.5rem" }}>
                  verifique seu e-mail.
                </h1>
                <p
                  style={{
                    fontSize: "var(--type-body)",
                    fontWeight: 300,
                    color: "var(--lp-ink)",
                    lineHeight: 1.7,
                    marginBottom: "1rem",
                  }}
                >
                  enviamos um link de confirmação para <strong>{email}</strong>.
                </p>
                <p
                  style={{
                    fontSize: "var(--type-body)",
                    fontWeight: 300,
                    color: "var(--lp-muted)",
                    lineHeight: 1.7,
                    marginBottom: "2rem",
                  }}
                >
                  clique no link do e-mail para ativar sua conta e continuar o cadastro.
                </p>
                <p style={{ fontSize: "var(--type-caption)", color: "var(--lp-muted)" }}>
                  não recebeu? verifique a pasta de spam ou{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setEmailSent(false);
                      setLoading(false);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontFamily: f,
                      fontSize: "var(--type-caption)",
                      color: "var(--lp-ink)",
                      textDecoration: "underline",
                      textUnderlineOffset: 3,
                      cursor: "pointer",
                    }}
                  >
                    tente novamente
                  </button>
                  .
                </p>
              </div>
            ) : (
              <>
                <h1 style={{ fontSize: "var(--type-h2)", fontWeight: 300, marginBottom: "2.5rem" }}>Criar conta</h1>

                {/* Social buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    onMouseEnter={() => setGoogleHover(true)}
                    onMouseLeave={() => setGoogleHover(false)}
                    style={{
                      width: "100%",
                      padding: "0.85rem",
                      fontFamily: f,
                      fontSize: "var(--type-label)",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      background: "transparent",
                      border: `0.5px solid ${googleHover ? HOVER_BLUE : "var(--lp-border2)"}`,
                      borderRadius: 0,
                      color: googleHover ? HOVER_BLUE : "var(--lp-ink)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.75rem",
                      transition: "border-color 0.2s ease, color 0.2s ease",
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    CONTINUAR COM GOOGLE
                  </button>
                  <button
                    type="button"
                    onClick={handleAppleLogin}
                    onMouseEnter={() => setAppleHover(true)}
                    onMouseLeave={() => setAppleHover(false)}
                    style={{
                      width: "100%",
                      padding: "0.85rem",
                      fontFamily: f,
                      fontSize: "var(--type-label)",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      background: "transparent",
                      border: `0.5px solid ${appleHover ? HOVER_BLUE : "var(--lp-border2)"}`,
                      borderRadius: 0,
                      color: appleHover ? HOVER_BLUE : "var(--lp-ink)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.75rem",
                      transition: "border-color 0.2s ease, color 0.2s ease",
                    }}
                  >
                    <svg width="14" height="16" viewBox="0 0 814 1000" fill="currentColor">
                      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 46.4 172.5 46.4 42.8 0 109.6-49 192.5-49 30.8 0 108.2 2.6 168.6 74.8zm-234.5-185.1c31.4-37.9 53.9-90.5 53.9-143.1 0-7.7-.6-15.4-1.9-21.7-50.6 1.9-110.4 33.7-146.1 75.8-28.2 32.1-54.5 84.7-54.5 138 0 8.3 1.3 16.6 1.9 19.2 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 133.1-69.5z" />
                    </svg>
                    CONTINUAR COM APPLE
                  </button>
                </div>

                {/* Divider */}
                <div style={{ display: "flex", alignItems: "center", gap: "1rem", margin: "1.5rem 0" }}>
                  <div style={{ flex: 1, borderTop: "0.5px solid var(--lp-border)" }} />
                  <span
                    style={{
                      fontSize: "var(--type-caption)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "var(--lp-muted)",
                    }}
                  >
                    OU
                  </span>
                  <div style={{ flex: 1, borderTop: "0.5px solid var(--lp-border)" }} />
                </div>

                {/* Form */}
                <form onSubmit={handleSignUp} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <div>
                    <label
                      style={{
                        fontFamily: f,
                        fontSize: "var(--type-caption)",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase" as const,
                        color: "var(--lp-muted)",
                        marginBottom: 6,
                        display: "block",
                      }}
                    >
                      ENDEREÇO DO SEU SITE
                    </label>
                    <input
                      value={username}
                      onChange={(e) => handleUsernameChange(e.target.value)}
                      required
                      style={inputStyle}
                      placeholder="ex: joao"
                    />
                    <div
                      style={{
                        fontFamily: f,
                        fontSize: "var(--type-caption)",
                        marginTop: 4,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span style={{ color: "var(--lp-muted)" }}>{username ? `${username}.criaaa.com` : "..."}</span>
                      {username.length > 0 && getUsernameError(username) ? (
                        <span style={{ color: "#FF3F2D" }}>{getUsernameError(username)}</span>
                      ) : isValidUsername(username) ? (
                        <span
                          style={{
                            color: checkingUsername ? "var(--lp-muted)" : usernameAvailable ? "#00E87A" : "#FF3F2D",
                          }}
                        >
                          {checkingUsername
                            ? "verificando..."
                            : usernameAvailable
                              ? "Disponível ✓"
                              : "Este endereço já está em uso"}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                  />
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="mínimo 8 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ ...inputStyle, paddingRight: "3.5rem" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: "absolute",
                        right: "1rem",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: f,
                        fontSize: "var(--type-caption)",
                        color: "var(--lp-muted)",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        padding: 0,
                      }}
                    >
                      {showPassword ? "OCULTAR" : "VER"}
                    </button>
                  </div>

                  {/* Password strength */}
                  {password.length > 0 && (
                    <div style={{ marginTop: 4, marginBottom: 4 }}>
                      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                        {[1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            style={{
                              flex: 1,
                              height: 2,
                              background: i <= strength ? strengthColor[strength] : "var(--lp-border2)",
                              transition: "background 0.2s ease",
                            }}
                          />
                        ))}
                        {strength > 0 && (
                          <span
                            style={{
                              fontSize: "var(--type-caption)",
                              textTransform: "uppercase",
                              color: "var(--lp-muted)",
                              marginLeft: 8,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {strengthLabel[strength]}
                          </span>
                        )}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        {checks.map((c) => (
                          <p
                            key={c.label}
                            style={{
                              fontSize: "var(--type-caption)",
                              color: c.ok ? "var(--lp-ink)" : "var(--lp-muted)",
                              lineHeight: 1.8,
                              margin: 0,
                            }}
                          >
                            {c.ok ? "✓" : "–"} {c.label}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  <input
                    type="password"
                    placeholder="confirmar senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    style={inputStyle}
                  />

                  {error && (
                    <p
                      style={{
                        fontSize: "var(--type-caption)",
                        color: "#C0392B",
                        letterSpacing: "0.06em",
                        marginTop: "0.75rem",
                        textTransform: "lowercase",
                      }}
                    >
                      {error}
                    </p>
                  )}

                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "0.75rem",
                      margin: "1.5rem 0 0",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="terms"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      style={{
                        marginTop: "0.15rem",
                        flexShrink: 0,
                        accentColor: "var(--lp-ink)",
                        width: 14,
                        height: 14,
                        cursor: "pointer",
                        borderRadius: 0,
                      }}
                    />
                    <label
                      htmlFor="terms"
                      style={{
                        fontFamily: f,
                        fontSize: "var(--type-caption)",
                        color: "var(--lp-muted)",
                        lineHeight: 1.7,
                        cursor: "pointer",
                      }}
                    >
                      li e concordo com os{" "}
                      <a
                        href="/termos"
                        target="_blank"
                        style={{
                          color: "var(--lp-ink)",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                        }}
                      >
                        termos de uso
                      </a>{" "}
                      e a{" "}
                      <a
                        href="/privacidade"
                        target="_blank"
                        style={{
                          color: "var(--lp-ink)",
                          textDecoration: "underline",
                          textUnderlineOffset: 3,
                        }}
                      >
                        política de privacidade
                      </a>
                      .
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    style={{
                      fontFamily: f,
                      fontSize: "var(--type-label)",
                      textTransform: "uppercase",
                      letterSpacing: "0.14em",
                      width: "100%",
                      background: "var(--lp-ink)",
                      color: "var(--lp-bg)",
                      border: "none",
                      borderRadius: 0,
                      padding: "0.9rem",
                      cursor: isFormValid && !loading ? "pointer" : "not-allowed",
                      opacity: isFormValid && !loading ? 1 : 0.4,
                      marginTop: "1.5rem",
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    {loading ? "CRIANDO..." : "CRIAR MINHA CONTA"}
                  </button>
                </form>

                <p style={{ fontSize: "var(--type-caption)", color: "var(--lp-muted)", marginTop: "1rem" }}>
                  já tem conta?{" "}
                  <Link
                    to="/entrar"
                    style={{ color: "var(--lp-ink)", textDecoration: "underline", textUnderlineOffset: 3 }}
                  >
                    entrar.
                  </Link>
                </p>

                <a
                  href="https://criaaa.com"
                  style={{
                    fontSize: "var(--type-caption)",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "var(--lp-muted)",
                    textDecoration: "none",
                    display: "block",
                    marginTop: "2rem",
                    transition: "color 0.2s ease",
                    fontFamily: f,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#0057FF")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--lp-muted)")}
                >
                  ← criaaa.com
                </a>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0px 1000px var(--lp-bg) inset !important;
          -webkit-text-fill-color: var(--lp-ink) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        @media (max-width: 768px) {
          .criar-conta-grid {
            grid-template-columns: 1fr !important;
          }
          .criar-conta-left {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
